/**
 * Tour Steps Configuration for Dashboard TourGuide
 * This file defines all the tour steps for the application
 * Static values are used here - can be replaced with API calls later
 */
import React from 'react';
import { useTour } from '@reactour/tour';
import {
	allowTourCompletionPersistence,
	triggerTourCompletionHandler,
	markTourAsCompleted,
	resetTourCompletionPersistence,
} from './helpers/tourGuideHelper';

const StepCounter = ({ current }) => {
	const { steps: activeSteps } = useTour();
	return <>{current} of {activeSteps ? activeSteps.length : ''}</>;
};

const steps = [
	{
		selector: '[data-tour="dashboard-welcome"]',
		content: ({ setIsOpen, currentStep, steps, setCurrentStep }) => {
			const isDarkTheme = document.documentElement.getAttribute('theme') === 'dark';
			return (
				<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
					<div style={{ marginBottom: '20px' }}>
						<h2 style={{
							margin: '0 0 10px 0',
							fontSize: '26px',
							fontWeight: '700',
							color: '#1a1a1a',
							background: 'linear-gradient(135deg, #3aba6f 0%, #2d8f52 100%)',
							WebkitBackgroundClip: 'text',
							WebkitTextFillColor: 'transparent',
							backgroundClip: 'text',
						}}>
							👋 Welcome!
						</h2>
						<p style={{
							margin: '0',
							fontSize: '13px',
							color: '#999999',
							fontWeight: '500',
							textTransform: 'uppercase',
							letterSpacing: '0.5px',
						}}>
							Step <StepCounter current={currentStep + 1} />
						</p>
					</div>
					<p style={{
						margin: '0 0 24px 0',
						fontSize: '15px',
						lineHeight: '1.8',
						color: '#4b5563',
						fontWeight: '500',
					}}>
						I'm your personal assistant. Let me give you a quick tour of all the features available on your dashboard.
					</p>
					<div style={{ 
						marginTop: '24px', 
						display: 'flex', 
						gap: '12px',
						justifyContent: 'space-between',
					}}>
						<button
							onClick={() => setIsOpen(false)}
							style={{
								padding: '10px 20px',
								backgroundColor: '#f0f0f0',
								color: '#1a1a1a',
								border: 'none',
								borderRadius: '8px',
								cursor: 'pointer',
								fontSize: '14px',
								fontWeight: '600',
								transition: 'all 0.3s ease',
								flex: 1,
								textAlign: 'center',
							}}
							onMouseEnter={(e) => {
								e.target.style.backgroundColor = '#e0e0e0';
								e.target.style.transform = 'translateY(-1px)';
							}}
							onMouseLeave={(e) => {
								e.target.style.backgroundColor = '#f0f0f0';
								e.target.style.transform = 'translateY(0)';
							}}
						>
							Skip Tour
						</button>
						<button
							onClick={() => setCurrentStep(currentStep + 1)}
							style={{
								padding: '10px 20px',
								backgroundColor: '#3aba6f',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								cursor: 'pointer',
								fontSize: '14px',
								fontWeight: '600',
								transition: 'all 0.3s ease',
								flex: 1,
								textAlign: 'center',
								boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
							}}
							onMouseEnter={(e) => {
								e.target.style.transform = 'translateY(-2px)';
								e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
							}}
							onMouseLeave={(e) => {
								e.target.style.transform = 'translateY(0)';
								e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
							}}
						>
							Next →
						</button>
					</div>
				</div>
			);
		},
	},
	{
		selector: '[data-tour="pending-appointments"]',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Pending Appointments</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This card shows all your upcoming appointments that are pending. Click on it to view the full list and manage your schedule.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '[data-tour="completed-appointments"]',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>✅ Completed Appointments</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					View your completed appointments here. This helps you track your business performance and customer satisfaction.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '[data-tour="cancelled-appointments"]',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>❌ Cancelled Appointments</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This section shows all your cancelled appointments. You can review and analyze cancellation patterns to improve your business.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '[data-tour="expired-appointments"]',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⏰ Expired Appointments</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					View appointments that have expired. This helps you understand appointment fulfillment and identify patterns.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '[data-tour="add-order-button"]',
		content: ({ setCurrentStep, currentStep, setIsOpen, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add Order</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use this button to quickly add new orders to your system. You can create new appointments or products from here.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to narrow pending appointments by employee, branch, service, or date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-pending-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Pending Appointments Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all pending appointments with key details and quick actions to manage them.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-completed-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to narrow completed appointments by employee, branch, service, or date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-completed-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>✅ Completed Appointments Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all completed appointments with key details and quick actions to manage them.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-cancelled-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to narrow cancelled appointments by employee, branch, service, or date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-cancelled-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>❌ Cancelled Appointments Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all cancelled appointments with key details. You can review cancellation reasons and analyze patterns to improve your business.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-expired-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to narrow expired appointments by employee, branch, service, or date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-expired-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⏰ Expired Appointments Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all expired appointments. Review them to understand appointment fulfillment and identify patterns.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-order-new-order-btn',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ New Order</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to create a new order. You can add appointments, products, apply promo codes, and manage customer details all in one place.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-order-filter-section',
		content: ({ setCurrentStep, currentStep, steps }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use filters to search and narrow orders by customer, branch, employee, date range, or order status.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-order-table',
		content: ({ setCurrentStep, currentStep, steps, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🛒 Orders Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all your orders with key details. Click on any order to view full details, manage payments, or take actions.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-service-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add Service</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to add a new service. You can set the name, price, duration, category, and more.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-service-search',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Services</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search box to quickly find services by name or category.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-service-grid',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🗂️ Services Grid</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All your services are displayed here. Click on a service card to edit, change its status, or manage its details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-category-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add Category</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to add a new service category. Categories help you organise your services.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-category-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search bar to quickly find categories by name, or adjust the number of items displayed per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-category-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🗂️ Categories Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all your service categories. Click the edit icon on any row to update the category name or status.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-product-upload-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Upload Product List</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use this button to bulk upload products via a CSV or ZIP file. This makes it easy to add many products at once.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-product-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📦 New Product</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click here to add a new product. Set the name, price, branch, images, and more.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-product-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Filter products by branch or search by name to quickly find what you need.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-product-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🛍️ Products Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All your products are listed here. You can edit, activate, deactivate, or delete products, and select multiple items for bulk actions.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-promotion-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🎁 New Promotion</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click here to create a new promotion. Set a promo code, discount, applicable services or products, and validity dates.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-promotion-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Search for promotions by name or adjust how many items are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-promotion-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Promotions Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All your promotions are listed here. You can view promo codes, date ranges, discounts, and edit or delete any promotion.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-customer-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Customers</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search bar to find customers by name, email, or other details. Adjust the items per page to control how many are shown at once.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-customer-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>👥 Customers Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All registered customers are listed here. You can view their details, contact info, and status, or click to edit their profile.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-employee-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add New Employee</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to add a new employee. Fill in their name, email, role, branch, and other required details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-employee-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Employees</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Filter employees by branch or use the search bar to find someone by name, email, or other details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-employee-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>👥 Employees Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All your employees are listed here with their ID, name, role, email, and status. Click the action icons to edit details, change passwords, or manage their schedule.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-admin-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add New Admin</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to add a new administrator. Fill in their name, email, password, role, and other required details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-admin-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Admins</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search bar to find administrators by name, email, or other details. You can also adjust how many entries are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-admin-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🛡️ Administrators Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All system administrators are listed here with their name, email, status, and other details. Use the action icons to edit or manage each administrator.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-shift-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>➕ Add New Shift</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click this button to create a new shift. Set the shift name, start time, and end time for your employees.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-shift-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔎 Search Shifts</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Search for shifts by name or adjust how many entries are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-shift-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🕐 Shifts Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					All shifts are listed here with their name, start time, end time, and status. Use the action icons to edit or delete a shift.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-worksched-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📅 Schedule Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select a branch, set the start and end dates, then click <strong>Apply Changes</strong> to load and save the worker schedule for that date range.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-worksched-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🗓️ Worker Schedule</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					The schedule grid shows each employee’s assigned shifts across the selected date range. Click on a date cell to assign or change a shift for that employee.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-leavereq-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search box to find specific leave requests by employee name or ID. You can also adjust how many records are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-leavereq-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Leave Requests List</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all employee leave requests along with the leave type, duration, date, and current status. Click the <strong>Edit</strong> icon on any row to view details and approve or reject the request.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-payroll-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Payroll Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select a branch and employee, then set the start and end dates for the payroll period. Click <strong>Search</strong> to load the payroll data for that employee.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-payroll-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>💰 Payroll Details</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This section shows the employee's payroll breakdown including base salary, attendance allowance, commissions, and the total payable amount. You can also view payroll history here.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-income-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Income Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select the date range and other filters to narrow down income records. Click <strong>Search</strong> to load the matching transactions.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-income-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>💵 Income Records</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all income transactions including the transaction ID, order ID or payee, payment date, and amount. Use the pagination controls to browse through records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-expenses-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>💸 Expenses</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click <strong>New Expenses</strong> to record a new expense entry. You can fill in the payee, amount, payment type, expense type, and other details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-expenses-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Expense Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select the date range and branch to filter expense records. Click <strong>Search</strong> to load the matching transactions.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-expenses-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Expenses Records</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all expense records including the ID, date, payee, payment type, expense type, and amount. Use the action buttons to view, edit, or delete any entry.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-cheque-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🧾 Cheque</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click <strong>Cheque</strong> to add a new cheque entry. You can fill in the payee, amount, cheque number, and other payment details.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-cheque-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Cheque Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select the date range and branch to filter cheque records. Click <strong>Search</strong> to load the matching transactions.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-cheque-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Cheque Records</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all cheque records including the ID, date, payee, cheque number, and amount. Use the action buttons to view, edit, or delete any entry.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-extype-add-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🏷️ Expense Types</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click <strong>Expenses Type</strong> to add a new expense category. These types are used when recording expenses to categorize your payments.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-extype-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Search & Filter</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the search box to quickly find a specific expense type by name. You can also control how many records are shown per page.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-extype-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Expense Type Records</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all expense type categories including the ID, name, and status. Use the edit button to update or activate/deactivate any type.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-orders-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📊 Orders Report Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Select the date range, status, and other filters to narrow down order records. Click <strong>Search</strong> to load the matching results, or <strong>Export</strong> to download the report.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-orders-export-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Export Orders Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					After searching, click <strong>Export</strong> to download the filtered orders report. The button is enabled only when there are results in the table.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-orders-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Orders Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table shows all order records matching your filters, including order ID, customer, date, status, and amount. Use the pagination controls to browse through results.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setIsOpen(false)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Report Appointments Tour Steps ──────────────────────────────────────────
	{
		selector: '#tour-report-appointments-export-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Export Appointments Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					After applying your filters, click <strong>Export</strong> to download the appointments report. The button is enabled only when results are available in the table.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-appointments-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📊 Appointments Report Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use these filters to narrow down your appointments report. Select a branch, employee, service, and date range, then click <strong>Search</strong> to load results.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-appointments-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Appointments Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Your filtered appointments are displayed here. Review the results and use pagination to browse through all records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Report Products Tour Steps ──────────────────────────────────────────────
	{
		selector: '#tour-report-products-export-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Export Products Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					After applying your filters, click <strong>Export</strong> to download the products report. The button is enabled only when there are results in the table.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-products-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📊 Products Report Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use these filters to narrow down your products report. Select a branch and date range, then click <strong>Search</strong> to load results.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-products-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Products Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Your filtered products are displayed here. Review the results and use pagination to browse through all records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Report Commission Tour Steps ────────────────────────────────────────────
	{
		selector: '#tour-report-commission-export-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Export Commission Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					After applying your filters, click <strong>Export</strong> to download the commission report. The button is enabled only when there are results in the table.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-commission-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📊 Commission Report Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use these filters to narrow down your commission report. Select a branch, employee, and date range, then click <strong>Search</strong> to load results.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-commission-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Commission Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Your filtered commission data is displayed here. Review each employee's commission details and use pagination to browse through all records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Report Payments Tour Steps ──────────────────────────────────────────────
	{
		selector: '#tour-report-payments-export-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Export Payments Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					After applying your filters, click <strong>Export</strong> to download the payments report. The button is enabled only when there are results in the table.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-payments-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📊 Payments Report Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use these filters to narrow down your payments report. Select a branch, payment method, and date range, then click <strong>Search</strong> to load results.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-payments-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Payments Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Your filtered payment records are displayed here. Review each transaction and use pagination to browse through all records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Report Schedule Tour Steps ──────────────────────────────────────────────
	{
		selector: '#tour-report-schedule-export-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Export Schedule Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					After applying your filters, click <strong>Export</strong> to download the schedule report. The button is enabled only when there are results in the table.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-schedule-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📊 Schedule Report Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use these filters to narrow down your schedule report. Select a branch, employee, and date range, then click <strong>Search</strong> to load results.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-schedule-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Schedule Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Your filtered schedule data is displayed here. Review each employee's scheduled shifts and use pagination to browse through all records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Report Attendance Tour Steps ────────────────────────────────────────────
	{
		selector: '#tour-report-attendance-export-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Export Attendance Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					After applying your filters, click <strong>Export</strong> to download the attendance report. The button is enabled only when there are results in the table.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-attendance-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📊 Attendance Report Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use these filters to narrow down your attendance report. Select a branch, employee, and date range, then click <strong>Search</strong> to load results.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-attendance-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Attendance Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Your filtered attendance records are displayed here. Review each employee's attendance and use pagination to browse through all records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Report Refunds Tour Steps ───────────────────────────────────────────────
	{
		selector: '#tour-report-refunds-export-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📤 Export Refunds Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					After applying your filters, click <strong>Export</strong> to download the refunds report. The button is enabled only when there are results in the table.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-refunds-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📊 Refunds Report Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use these filters to narrow down your refunds report. Select a status, branch, and date range, then click <strong>Search</strong> to load results.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-report-refunds-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Refunds Report</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Your filtered refund records are displayed here. Review each refund's details and use pagination to browse through all records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Refund Requested Tour Steps ─────────────────────────────────────────────
	{
		selector: '#tour-refund-requested-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Refund Requested</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all pending refund requests. Each row shows the order details, customer name, paid amount, and the requested refund amount.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-refund-requested-action-btn',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⚙️ Refund Action</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click the action button on any row to open the refund details panel. From there you can enter the refund amount, confirm the refund, or reject the request.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Completed Refunds Tour Steps ────────────────────────────────────────────
	{
		selector: '#tour-completed-refunds-filter-section',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔍 Completed Refunds Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the <strong>Status</strong> dropdown to filter by Paid or Rejected refunds, and the search box to find records by keyword.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-completed-refunds-table',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Completed Refunds</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table shows all processed refunds. Each row displays the order details, refund amount, and the final status — <strong style={{ color: '#1e6c1e' }}>Paid</strong> or <strong style={{ color: '#AB232F' }}>Rejected</strong>.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							setIsOpen(false);
							markTourAsCompleted();
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Branches Page Tour Steps ───────────────────────────────────────────────
	{
		selector: '#tour-branch-new-button',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🏢 New Branch</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use this button to add a new branch to your business. Fill in the branch details such as name, address, and contact information.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 20px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-branch-table',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Branches Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all your branches with details like branch name, address, contact numbers, and status. You can search and paginate through the records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	// ─── Calendar Page Tour Steps ────────────────────────────────────────────────
	{
		selector: '#tour-calendar-filters',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🌿 Branch & Employee Filters</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use these dropdowns to filter the calendar by <strong>Branch</strong> and <strong>Employee</strong>. Select a branch first to load its staff, then choose a specific employee to see only their appointments — or leave it on <em>Any</em> to view everyone at once.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-calendar-view-modes',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🗓️ Calendar View Modes</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Switch between <strong>Month</strong>, <strong>Week</strong>, <strong>Work Week</strong>, <strong>Day</strong>, and <strong>Agenda</strong> views using these buttons. Choose the view that best suits your scheduling workflow — Day and Week views give you time-slot precision, while Month and Agenda give a broader overview.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-calendar-main-card',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📅 Main Calendar</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This is the <strong>Main Calendar</strong>. <strong>Click any appointment event</strong> to open its detail panel with the full service, employee, and time information. <strong>Click an empty time slot</strong> to create a new appointment — a form will slide in where you can set the service, date/time, and assigned employee. Use the navigation arrows to move between dates.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							markTourAsCompleted();
							setIsOpen(false);
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Help & Support Page Tour Steps ─────────────────────────────────────────
	{
		selector: '#tour-help-contact-us-card',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📬 Contact Us</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use this form to reach our support team directly. Select a <strong>Subject</strong> that best describes your issue, compose your <strong>Message</strong> in the rich-text editor, and optionally <strong>attach files</strong> (images, PDFs, spreadsheets — up to 6 files, 5 MB each) to provide extra context.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-help-send-message-button',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>✉️ Send Message</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Once your subject and message are ready, click <strong>Send Message</strong> to submit your support ticket. Our team will receive it immediately and respond as soon as possible. The button is disabled while a submission is in progress to prevent duplicate requests.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							markTourAsCompleted();
							setIsOpen(false);
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Pricing Page Tour Steps ─────────────────────────────────────────────────
	{
		selector: '#tour-pricing-request-custom-domain',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🌐 Request Custom Domain</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click <strong>Request Custom Domain</strong> to open a pre-filled support ticket asking our team to connect a custom domain (e.g. <em>book.yourbusiness.com</em>) to your customer-facing website. Your request is sent directly to support with all necessary context.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-pricing-plans-grid',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📦 Upgrade or Downgrade Plan</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Browse all available subscription plans here. Each card shows the plan's features, pricing, and user limits. Click <strong>Upgrade to This Plan</strong> on any higher-tier card to move to a better plan, or <strong>Request to Downgrade</strong> on a lower-tier card to raise a support-assisted downgrade request.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							markTourAsCompleted();
							setIsOpen(false);
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Settings Page Tour Steps ───────────────────────────────────────────────
	{
		selector: '#tour-settings-tab-company-profile',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🏢 Company Profile</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Configure your <strong>Company Profile</strong> here — set your business name, logos, contact details, and general company information that appears across the platform.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-settings-tab-website-config',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🌐 Website Config</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Manage your <strong>Website Configuration</strong> — control domain settings, SEO metadata, theme colours, and other web-facing options for your customer portal.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-settings-tab-customer-web',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>👤 Customer Web Settings</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Configure <strong>Customer Web Settings</strong> — define registration fields, required inputs, and options that customers see and interact with on the web portal.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-settings-tab-additional-fields',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔧 Additional Fields</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Create and manage <strong>Additional Fields</strong> — add custom data fields to bookings, customer profiles, or orders to capture any extra information your business needs.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-settings-tab-social-media',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📱 Social Media Platforms</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Link your <strong>Social Media Platforms</strong> — add your Facebook, Instagram, Twitter, and other social media URLs to display on the customer-facing web portal.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-settings-tab-customer-app',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📲 Customer APP Setting</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Customise the <strong>Customer Mobile App</strong> — set the app logo, splash screen, app store links, primary colours, and other branding options for the customer-facing app.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-settings-tab-employee-app',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>👷 Employee APP Setting</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Configure the <strong>Employee Mobile App</strong> — manage branding assets, theme settings, and visual options for the app used by your staff and field employees.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-settings-tab-bank-details',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🏦 Bank Details</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Enter your <strong>Bank Details</strong> — add your business bank account information used for payment processing, invoices, and financial reporting within the platform.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							markTourAsCompleted(14);
							setIsOpen(false);
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Sliders Page Tour Steps ────────────────────────────────────────────────
	{
		selector: '#tour-slider-new-button',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🖼️ New Slider</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Click here to add a new slide to your business homepage. You can upload separate images for Desktop, Tablet, and Mobile views, set a redirect URL, assign it to one or more branches, and control its publish status (Draft / Publish).
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 20px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-slider-table',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Sliders Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table displays all your configured slides. Each row shows the assigned branch, the redirect URL, and the current publish status — active (✔) or inactive (✖). Use the search bar above to quickly filter slides by any field.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-slider-action-button',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⚙️ Action Buttons</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the <strong>Edit</strong> button (✏️) to update a slide's image, URL, branch assignment, or publish status. Use the <strong>Delete</strong> button (🗑️) to permanently remove a slide from the list.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							markTourAsCompleted();
							setIsOpen(false);
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Roles Page Tour Steps ──────────────────────────────────────────────────
	{
		selector: '#tour-roles-new-button',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🔑 New Role</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use this button to create a new role. You can define the role name, description, and assign specific permissions to it.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 20px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-roles-table',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>📋 Roles Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table lists all your roles with their name, description, number of members, and status. You can search and paginate through the records.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-roles-action-button',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⚙️ Action Buttons</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the action buttons to edit role permissions, update role details, or change the role status.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							markTourAsCompleted();
							setIsOpen(false);
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Invoices Page Tour Steps ───────────────────────────────────────────────
	{
		selector: '#tour-invoice-table',
		content: ({ setCurrentStep, currentStep }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>🧾 Invoices Table</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /></p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					This table shows all your subscription invoices including the package name, amount, invoice date, paid date, and payment status.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep + 1)}
						style={{
							padding: '10px 20px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						Next →
					</button>
				</div>
			</div>
		),
	},
	{
		selector: '#tour-invoice-action-button',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⚙️ Action Buttons</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the action buttons to download the invoice as a PDF or make a payment for pending invoices.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							markTourAsCompleted();
							setIsOpen(false);
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
	// ─── Branches Page Tour Steps ────────────────────────────────────────────────
	{
		selector: '#tour-branch-action-button',
		content: ({ setCurrentStep, currentStep, setIsOpen }) => (
			<div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
				<div style={{ marginBottom: '16px' }}>
					<h3 style={{
						margin: '0 0 8px 0',
						fontSize: '20px',
						fontWeight: '700',
						color: '#1a1a1a',
					}}>⚙️ Action Button</h3>
					<p style={{
						margin: '0',
						fontSize: '13px',
						color: '#999999',
						fontWeight: '500',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
					}}>Step <StepCounter current={currentStep + 1} /> - Final</p>
				</div>
				<p style={{
					margin: '0 0 24px 0',
					fontSize: '15px',
					lineHeight: '1.8',
					color: '#4b5563',
					fontWeight: '500',
				}}>
					Use the edit button in the Action column to update branch details such as name, address, contact numbers, and status.
				</p>
				<div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
					<button
						onClick={() => setCurrentStep(currentStep - 1)}
						style={{
							padding: '10px 18px',
							backgroundColor: '#f0f0f0',
							color: '#1a1a1a',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = '#e0e0e0';
							e.target.style.transform = 'translateY(-1px)';
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = '#f0f0f0';
							e.target.style.transform = 'translateY(0)';
						}}>
						← Back
					</button>
					<button
						onClick={() => {
							markTourAsCompleted();
							setIsOpen(false);
						}}
						style={{
							padding: '10px 18px',
							backgroundColor: '#3aba6f',
							color: 'white',
							border: 'none',
							borderRadius: '8px',
							cursor: 'pointer',
							fontSize: '14px',
							fontWeight: '600',
							transition: 'all 0.3s ease',
							flex: 1,
							textAlign: 'center',
							boxShadow: '0 4px 12px rgba(58, 186, 111, 0.3)',
						}}
						onMouseEnter={(e) => {
							e.target.style.transform = 'translateY(-2px)';
							e.target.style.boxShadow = '0 6px 16px rgba(58, 186, 111, 0.4)';
						}}
						onMouseLeave={(e) => {
							e.target.style.transform = 'translateY(0)';
							e.target.style.boxShadow = '0 4px 12px rgba(58, 186, 111, 0.3)';
						}}>
						✓ Finish Tour
					</button>
				</div>
			</div>
		),
	},
];

const normalizeTourSteps = (stepList = []) =>
	stepList.map((step) => {
		if (typeof step?.content !== 'function') {
			return step;
		}

		return {
			...step,
			content: (tourProps) =>
				step.content({
					...tourProps,
					setIsOpen: (value) => {
						if (value === false) {
							const isFinalStep =
								typeof tourProps.currentStep === 'number' &&
								Array.isArray(tourProps.steps) &&
								tourProps.currentStep === tourProps.steps.length - 1;

							if (isFinalStep) {
								allowTourCompletionPersistence();
								void triggerTourCompletionHandler();
							} else {
								resetTourCompletionPersistence();
							}
						}

						tourProps.setIsOpen(value);
					},
				}),
		};
	});

const normalizedSteps = normalizeTourSteps(steps);

export const styles = {
	popover: (base) => ({
		...base,
		backgroundColor: '#ffffff',
		color: '#1a1a1a',
		borderRadius: '12px',
		padding: '24px',
		boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
		maxWidth: '420px',
		fontFamily: 'system-ui, -apple-system, sans-serif',
		transform: 'translateY(0)',
		transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
	}),
	badge: (base) => ({
		...base,
		backgroundColor: '#3aba6f',
		color: '#ffffff',
		fontSize: '12px',
		borderRadius: '6px',
		padding: '6px 10px',
		fontWeight: '600',
	}),
	maskArea: (base) => ({
		...base,
		rx: 12,
		fill: 'rgba(0, 0, 0, 0.15)',
	}),
	maskRect: (base) => ({
		...base,
		fill: 'rgba(0, 0, 0, 0.15)',
	}),
	highlightedArea: (base) => ({
		...base,
		boxShadow: '0 0 0 4px rgba(58, 186, 111, 0.2)',
		borderRadius: '12px',
		transition: 'all 0.3s ease',
	}),
};

export default normalizedSteps;
